import { AppState, Platform } from "@-/com.npmjs.react-native";
import { useEffect, useState } from "react";

/**
 * How long a hidden route body keeps its rows, native views and query observers. Coming straight
 * back (a quick look at a character, a peek at another tab) keeps the rendered body; a longer
 * absence trades one render on return for the memory.
 */
export const HIDDEN_ROUTE_BODY_RELEASE_DELAY_MS = 30_000;

/**
 * Whether a body should stay mounted: always while visible, then for `delayMs` after it is hidden
 * (`null`: for as long as it stays hidden). An iOS memory warning releases a hidden body
 * immediately. A body that mounts hidden starts released unless `retainInitially` (then the grace
 * period starts at mount).
 */
export function useHiddenBodyRetention(
  isVisible: boolean,
  {
    delayMs = HIDDEN_ROUTE_BODY_RELEASE_DELAY_MS,
    retainInitially = false,
  }: { delayMs?: number | null; retainInitially?: boolean } = {},
): boolean {
  const [isRetained, setIsRetained] = useState(isVisible || retainInitially);
  if (isVisible && !isRetained) {
    // Latch during this render so the returning frame already has its body.
    setIsRetained(true);
  }
  useEffect(() => {
    if (isVisible || !isRetained) {
      return;
    }
    const timer =
      delayMs === null
        ? undefined
        : setTimeout(() => {
            setIsRetained(false);
          }, delayMs);
    // react-native-web's AppState implements only "change": subscribing to "memoryWarning" there
    // returns undefined, so an unconditional `.remove()` threw in this cleanup and the route's error
    // boundary replaced the page (web Browse → Levels → Characters → back). The browser has no
    // memory warnings to listen for anyway.
    const memoryWarning =
      Platform.OS === "web"
        ? undefined
        : AppState.addEventListener("memoryWarning", () => {
            setIsRetained(false);
          });
    return () => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      memoryWarning?.remove();
    };
  }, [delayMs, isRetained, isVisible]);
  return isVisible || isRetained;
}
